import { useEffect, useRef, useState } from "react";
import { Mic, Loader2, X, MicOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { getLang } from "@/i18n";
import { MicHelpDialog } from "@/components/agent/MicHelpDialog";

const TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
const pickType = () => TYPES.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
const ext = (t) => (t.includes("mp4") ? "mp4" : t.includes("ogg") ? "ogg" : "webm");
const HOLD_MS = 450;
const MAX_MS = 120000;
const HINT_KEY = "mic-hint-uses";
const errKind = (e) => (["NotFoundError", "OverconstrainedError"].includes(e?.name) ? "nodevice" : e?.name === "NotReadableError" ? "busy" : "denied");
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Microfono: tieni premuto e parla (rilascia per inviare) oppure tocca per iniziare e tocca di nuovo per inviare.
export const VoiceButton = ({ onText, disabled, showHint = true }) => {
  const [state, setState] = useState("idle");
  const [hold, setHold] = useState(false);
  const [secs, setSecs] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const [help, setHelp] = useState(null);
  const [hint, setHint] = useState(() => Number(localStorage.getItem(HINT_KEY) || 0) < 3);
  const rec = useRef(null);
  const starting = useRef(false);
  const press = useRef({ at: 0, down: false });

  useEffect(() => () => {
    const r = rec.current;
    if (r) { r.cancel = true; if (r.mr.state !== "inactive") r.mr.stop(); }
  }, []);
  useEffect(() => {
    navigator.permissions?.query?.({ name: "microphone" }).then((p) => {
      setBlocked(p.state === "denied");
      p.onchange = () => setBlocked(p.state === "denied");
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (state !== "rec") return undefined;
    setSecs(0);
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  const upload = async (blob, type) => {
    setState("busy");
    try {
      const fd = new FormData();
      fd.append("file", blob, `voce.${ext(type)}`);
      fd.append("lang", getLang());
      const { data } = await api.post("/agent/transcribe", fd);
      onText(data.text);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Messaggio vocale non riuscito");
    } finally { setState("idle"); }
  };

  const stop = (cancel = false) => {
    const r = rec.current;
    if (!r) return;
    r.cancel = cancel;
    clearTimeout(r.timer);
    if (r.mr.state !== "inactive") r.mr.stop();
  };

  const start = async () => {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { setHelp("unsupported"); return; }
    starting.current = true;
    setState("wait");
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (e) { starting.current = false; setState("idle"); press.current.down = false; setHelp(errKind(e)); return; }
    setBlocked(false);
    navigator.vibrate?.(30);
    const type = pickType();
    const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks = [];
    const r = { mr, cancel: false, timer: setTimeout(() => stop(), MAX_MS) };
    mr.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    mr.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      rec.current = null;
      if (r.cancel) { setState("idle"); return; }
      const t = mr.mimeType || type || "audio/webm";
      upload(new Blob(chunks, { type: t }), t);
    };
    mr.start(250);
    rec.current = r;
    starting.current = false;
    setHold(press.current.down);
    setState("rec");
    const uses = Number(localStorage.getItem(HINT_KEY) || 0) + 1;
    localStorage.setItem(HINT_KEY, String(uses));
    setHint(uses < 3);
  };

  const onDown = (e) => {
    if (disabled || state === "busy" || starting.current) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (rec.current) { press.current.down = false; stop(); return; }
    press.current = { at: Date.now(), down: true };
    start();
  };
  const onUp = () => {
    if (!press.current.down) return;
    press.current.down = false;
    if (rec.current && Date.now() - press.current.at >= HOLD_MS) stop();
    else setHold(false);
  };
  const onKey = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    if (rec.current) stop(); else if (!starting.current && state === "idle") start();
  };

  const recording = state === "rec";
  const pill = "absolute bottom-full right-0 mb-2 flex items-center gap-2 rounded-full text-sm font-semibold px-3 py-2 shadow-lg whitespace-nowrap animate-in-up";
  return (
    <div className="relative shrink-0">
      {recording && (
        <div className={`${pill} bg-red-600 text-white pr-1.5`} data-testid="agent-voice-recording">
          <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
          <span className="tabular-nums" data-testid="agent-voice-timer">{mmss(secs)}</span>
          {hold ? "Ti ascolto… rilascia per inviare" : "Ti ascolto… tocca di nuovo per inviare"}
          <button type="button" onClick={() => stop(true)} className="p-2 rounded-full hover:bg-white/20" aria-label="Annulla" data-testid="agent-voice-cancel"><X className="w-4 h-4" /></button>
        </div>
      )}
      {state === "wait" && <div className={`${pill} bg-slate-900 text-white`} data-testid="agent-voice-wait"><Loader2 className="w-4 h-4 animate-spin" /> Consenti l'uso del microfono…</div>}
      {state === "busy" && <div className={`${pill} bg-slate-900 text-white`} data-testid="agent-voice-busy"><Loader2 className="w-4 h-4 animate-spin" /> Trascrivo…</div>}
      {state === "idle" && hint && showHint && !disabled && (
        <div className={`${pill} bg-primary text-primary-foreground pointer-events-none`} data-testid="agent-voice-hint">
          <Mic className="w-4 h-4" /> Tieni premuto per parlare
        </div>
      )}
      <button type="button" disabled={disabled || state === "busy"} onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onUp}
        onKeyDown={onKey} onContextMenu={(e) => e.preventDefault()} aria-label="Tieni premuto per parlare" title="Tieni premuto per parlare"
        style={{ touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
        className={`relative h-11 w-11 rounded-md flex items-center justify-center select-none transition-[background-color,transform,box-shadow] duration-150 disabled:opacity-50 ${
          recording ? "bg-red-600 text-white scale-110 shadow-[0_0_0_8px_rgba(220,38,38,0.25)] animate-pulse" : "border border-input bg-background hover:bg-secondary text-foreground"}`}
        data-testid="agent-mic-button">
        {state === "busy" || state === "wait" ? <Loader2 className="w-4 h-4 animate-spin" /> : blocked ? <MicOff className="w-[18px] h-[18px] text-destructive" /> : <Mic className="w-[18px] h-[18px]" />}
      </button>
      {help && <MicHelpDialog kind={help} onClose={() => setHelp(null)} onRetry={() => { setHelp(null); start(); }} />}
    </div>
  );
};

export default VoiceButton;
